<template>
  <div class="i-user-meeting-feedback">
    <div
      class="main"
      @click="data.showFeedbackModal = true"
    >
      <template v-if="!!props.userMeetingScore">
        <img
          alt="Filled star for rated session"
          :src="iconStarFilled"
        />
        <div>
          <span class="score">{{ props.userMeetingScore }}</span
          >/5
        </div>
      </template>
      <template v-else
        ><img
          alt="Empty star for unrated session"
          :src="iconStarOutline"
        />
        <span class="text">Rate Session</span></template
      >
    </div>

    <iBaseModal
      class="modal"
      :visible="data.showFeedbackModal"
      :fully-customisable="true"
      :enable-bg-close="true"
      max-width="820px"
      @click-on-close="closeAndResetFeedbackModal"
    >
      <div class="feedback-modal">
        <div class="intro">
          <div>Give Feedback</div>
          <div class="sub">
            <template v-if="isEditable">How was your {{ props.meetingTitle }} on {{ props.meetingDate }}?</template>
            <template v-else>Your feedback for this session is no longer editable.</template>
          </div>
        </div>
        <div class="rater">
          <div
            v-for="info in SCORE_INFO"
            :key="info.score"
            class="rate"
            @click="isEditable ? (data.feedbackScore = info.score) : null"
          >
            <div
              class="face"
              :class="{ active: data.feedbackScore === info.score, disabled: !isEditable }"
            >
              <img
                alt="User face emojis for each rating"
                :src="info.icon"
              />
            </div>
            <div class="title">{{ info.title }}</div>
          </div>
        </div>
        <div class="note">
          <label>Any other comments? (Optional)</label>
          <textarea
            v-model="data.feedbackNote"
            class="text-area"
            :disabled="!isEditable"
            autofocus
          ></textarea>
        </div>
        <div class="actions">
          <iBaseButton
            v-bind="otherButtonStyle"
            :text="isEditable ? 'Not Now' : 'Close'"
            @clicked="closeAndResetFeedbackModal"
          />
          <iBaseButton
            v-if="isEditable"
            v-bind="mainButtonStyle"
            :is-disabled="!data.feedbackScore"
            text="Submit"
            icon-width="22px"
            @clicked="isEditable ? submitUserFeedback() : null"
          />
        </div>
      </div>
    </iBaseModal>

    <iBaseModal
      class="modal"
      :visible="data.showConfirmModal"
      :fully-customisable="true"
      :enable-bg-close="true"
      max-width="600px"
      @click-on-close="data.showConfirmModal = false"
    >
      <div class="confirm-modal">
        <div class="intro">
          <div>Thanks for your feedback!</div>
          <div class="sub">Your input helps us support our coaches and improve the experience for future sessions</div>
        </div>
        <img
          alt="Thumbs up for completing your feedback for this session"
          :src="ImageThumb"
        />
        <iBaseButton
          class="action"
          v-bind="mainButtonStyle"
          :width="isTabletOrLarger ? '350px' : '100%'"
          text="Back to Coaching"
          @clicked="data.showConfirmModal = false"
        />
      </div>
    </iBaseModal>

    <iBaseModal
      class="modal"
      :visible="data.showErrorModal"
      :fully-customisable="true"
      :enable-bg-close="true"
      max-width="400px"
      @click-on-close="data.showErrorModal = false"
    >
      <div class="error-modal">
        <div class="modal-top">
          <div class="header">Error!</div>
          Sorry, there was an error completing this action. Please try again or contact support if the issue persists.
        </div>
        <div class="bottom">
          <iBaseButton
            v-bind="mainButtonStyle"
            :background-color="bittersweet500"
            text="Close"
            @clicked="data.showErrorModal = false"
          />
        </div>
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { astronaut500, bittersweet500, black500, white } from "@/styles/colors"
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import api from "@/services/apiClient"

import iBaseModal from "@/components/base/BaseModal.vue"
import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import iconFaceSad from "@/assets/icons/icon-face-sad.svg"
import iconFaceFrown from "@/assets/icons/icon-face-frown.svg"
import iconFaceNeutral from "@/assets/icons/icon-face-neutral.svg"
import iconFaceContent from "@/assets/icons/icon-face-content.svg"
import iconFaceHappy from "@/assets/icons/icon-face-happy.svg"
import iconStarFilled from "@/assets/icons/icon-star-blue-filled.svg"
import iconStarOutline from "@/assets/icons/icon-star-blue-outline.svg"
import ImageThumb from "@/assets/images/thumb-up-gif.gif"

// TYPES
import { MeetingScore } from "@/shared/types/MeetingNotes"

// EMITS
const emit = defineEmits(["refresh-meeting-notes"])

// PROPS
const props = defineProps({
  meetingNoteId: {
    type: Number,
    required: true
  },
  userMeetingScore: {
    type: Number as () => MeetingScore,
    default: null
  },
  userMeetingNotes: {
    type: String,
    default: null
  },
  meetingTitle: {
    type: String,
    required: true
  },
  meetingDate: {
    type: String,
    required: true
  },
  createdAt: {
    type: Date,
    default: null
  },
  showImmediately: {
    type: Boolean,
    default: false
  }
})

// COMPOSABLES
const isTabletOrLarger = useIsScreenSize(breakpointMd)

// CONSTANTS
const SCORE_INFO: { icon: string; score: MeetingScore; title: string }[] = [
  { icon: iconFaceSad, score: 1, title: "Poor" },
  { icon: iconFaceFrown, score: 2, title: "Not Great" },
  { icon: iconFaceNeutral, score: 3, title: "Okay" },
  { icon: iconFaceContent, score: 4, title: "Good" },
  { icon: iconFaceHappy, score: 5, title: "Great" }
]

// DATA
const data = reactive({
  showFeedbackModal: false,
  showConfirmModal: false,
  showErrorModal: false,
  feedbackNote: props.userMeetingNotes,
  feedbackScore: props.userMeetingScore
})

// COMPUTED
const mainButtonStyle = computed(() => {
  return {
    backgroundColor: astronaut500,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isTabletOrLarger.value ? "180px" : "100%",
    size: isTabletOrLarger.value ? "medium" : "small"
  }
})
const otherButtonStyle = computed(() => {
  return {
    backgroundColor: "transparent",
    borderRadius: "8px",
    fontWeight: "500",
    textColor: black500,
    width: isTabletOrLarger.value ? "180px" : "100%",
    size: isTabletOrLarger.value ? "medium" : "small",
    version: "outline"
  }
})
const isEditable = computed(() => !props.createdAt || props.createdAt > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))

// FUNCTIONS
const submitUserFeedback = async () => {
  if (data.feedbackScore) {
    try {
      await api.meeting.patchUserFeedback(props.meetingNoteId, {
        userMeetingScore: data.feedbackScore,
        userMeetingNotes: data.feedbackNote
      })

      emit("refresh-meeting-notes")
      data.showFeedbackModal = false
      data.showConfirmModal = true
    } catch (error) {
      console.error("Error updating this meeting note feedback", error)
      data.showErrorModal = true
    }
  }
}

const closeAndResetFeedbackModal = () => {
  data.showFeedbackModal = false
  data.feedbackNote = props.userMeetingNotes
  data.feedbackScore = props.userMeetingScore
}

// LIFECYCLE HOOKS
onMounted(() => {
  if (props.showImmediately) {
    if (isEditable.value && !props.userMeetingScore) data.showFeedbackModal = true
    localStorage.setItem("last-triggered-meeting-id", props.meetingNoteId.toString())
  }
})
</script>

<style lang="scss" scoped>
.i-user-meeting-feedback {
  .main {
    @include flex(row, end, normal, $gap: 4px);
    @include text-sm;

    font-weight: 500;
    color: $azure-radiance600;
    cursor: pointer;

    .score {
      @include text-md;

      font-weight: 700;
    }

    .text {
      text-decoration: underline;
    }
  }

  .modal {
    padding: 0;
  }

  .intro {
    @include flex(column, center, center, $gap: 10px);
    @include text-xl;

    text-align: center;
    font-weight: 600;

    @media screen and (min-width: $breakpoint-md) {
      $gap: 20px;

      @include display-xs;
    }

    .sub {
      @include text-md;

      font-weight: 400;

      @media screen and (min-width: $breakpoint-md) {
        @include text-lg;
      }
    }
  }

  .feedback-modal {
    @include flex(column, start, center, $gap: 40px);

    margin: 30px 15px;

    @media screen and (min-width: $breakpoint-md) {
      margin: 30px;
    }

    .rater {
      @include flex(row, space-between, start, $gap: 5px);

      text-align: center;
      min-width: 90%;

      .rate {
        @include flex(column, start, center, $gap: 10px);

        cursor: pointer;

        .face {
          @include border-radius-rounded;

          padding: 5px;

          @media screen and (min-width: $breakpoint-sm) {
            padding: 10px;
          }

          @media screen and (min-width: $breakpoint-md) {
            padding: 20px;
          }

          &.active {
            background-color: $astronaut200;
          }

          &.disabled {
            cursor: not-allowed;
          }
        }

        .title {
          @include text-sm;

          font-weight: 500;

          @media screen and (min-width: $breakpoint-md) {
            @include text-md;
          }
        }

        &:hover {
          .face:not(.disabled, .active) {
            background-color: $astronaut100;
          }
        }
      }
    }

    .note {
      @include flex(column, start, start, $gap: 5px);

      min-width: 90%;

      label {
        font-weight: 500;
      }

      .text-area {
        @include border-radius-sm;

        border: 1px solid $black200;
        width: 100%;
        min-height: 130px;
        padding: 8px 10px;
        resize: none;
        field-sizing: content;
        max-height: 250px;

        &:focus-visible {
          outline: none;
          border: 1px solid $black500;
        }
      }
    }

    .actions {
      @include flex(column, center, center, $gap: 15px);

      width: 100%;

      @media screen and (min-width: $breakpoint-md) {
        @include flex(row, center, center, $gap: 35px);
      }
    }
  }

  .confirm-modal {
    @include flex(column, center, center);

    margin: 30px;

    img {
      max-width: 250px;
      max-height: 250px;
    }

    .action {
      margin-top: 20px;
    }
  }

  .error-modal {
    .modal-top {
      @include flex(column, center, center, $gap: 15px);
      @include text-lg;

      text-align: center;
      font-weight: 500;
      margin: 25px;
      color: $bittersweet600;

      .header {
        @include text-xl;
      }
    }

    .bottom {
      @include flex(row, center, center, $gap: 25px);

      background-color: $black50;
      padding: 12px 25px;
      border-bottom-left-radius: 16px;
      border-bottom-right-radius: 16px;

      button {
        padding: 8px 10px;
      }
    }
  }
}
</style>
